import '@/styles/globals.scss'
import { SiteSettingsProvider } from '@/components/site-settings'
import '@/styles/text.scss'
import '@/styles/stealth-theme.scss'
import '@/styles/retro-theme.scss'
import { gql } from '@apollo/client'
import { ApolloProvider } from '@apollo/client/react'
import { MeProvider } from '@/components/me'
import PlausibleProvider from 'next-plausible'
import getApolloClient from '@/lib/apollo.js'
import { PriceProvider } from '@/components/price'
import { BlockHeightProvider } from '@/components/block-height'
import Head from 'next/head'
import { useRouter } from 'next/dist/client/router'
import { useCallback, useEffect } from 'react'
import { ShowModalProvider } from '@/components/modal'
import ErrorBoundary from '@/components/error-boundary'
import { AnimationProvider } from '@/components/animation'
import { ToastProvider } from '@/components/toast'
import { ServiceWorkerProvider } from '@/components/serviceworker'
import { SSR } from '@/lib/constants'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import dynamic from 'next/dynamic'
import { HasNewNotesProvider } from '@/components/use-has-new-notes'
import { BrandingProvider } from '@/components/territory-branding'
import { WalletsProvider } from '@/wallets/client/hooks'
import FaviconProvider from '@/components/favicon'
import { CookiesProvider } from '@/components/use-cookie'
import { patchDOMTranslations } from '@/lib/patch-dom-translate'

const PWAPrompt = dynamic(() => import('react-ios-pwa-prompt'), { ssr: false })

patchDOMTranslations()

NProgress.configure({
  showSpinner: false
})

function writeQuery (client, apollo, data) {
  if (apollo && data) {
    client.writeQuery({
      query: gql`${apollo.query}`,
      data,
      variables: apollo.variables,
      overwrite: SSR
    })
  }
}

export default function MyApp (props) {
  if (props.Component.adminPage) return <><Head><meta name='viewport' content='initial-scale=1.0, width=device-width, viewport-fit=cover' /></Head><ErrorBoundary><props.Component {...props.pageProps} /></ErrorBoundary></>
  return <PublicApp {...props} />
}

function PublicApp ({ Component, pageProps: { ...props } }) {
  const client = getApolloClient()
  const router = useRouter()

  const shouldShowProgressBar = useCallback((newPathname, shallow) => {
    return !shallow || newPathname !== router.pathname
  }, [router.pathname])

  useEffect(() => {
    const nprogressStart = (newPathname, { shallow }) => {
      shouldShowProgressBar(newPathname, shallow) && NProgress.start()
    }
    const nprogressDone = (newPathname, { shallow }) => {
      NProgress.done()
    }

    router.events.on('routeChangeStart', nprogressStart)
    router.events.on('routeChangeComplete', nprogressDone)
    router.events.on('routeChangeError', nprogressDone)

    if (!props?.apollo) return
    // HACK: 'cause there's no way to tell Next to skip SSR
    // So every page load, we modify the route in browser history
    // to point to the same page but without SSR, ie ?nodata=true
    // this nodata var will get passed to the server on back/foward and
    // 1. prevent data from reloading and 2. perserve scroll
    // (2) is not possible while intercepting nav with beforePopState
    router.replace({
      pathname: router.pathname,
      query: { ...router.query, nodata: true }
    }, router.asPath, { ...router.options, shallow: true }).catch((e) => {
      // workaround for https://github.com/vercel/next.js/issues/37362
      if (!e.cancelled) {
        console.log(e)
        throw e
      }
    })

    return () => {
      router.events.off('routeChangeStart', nprogressStart)
      router.events.off('routeChangeComplete', nprogressDone)
      router.events.off('routeChangeError', nprogressDone)
    }
  }, [router.asPath, props?.apollo, shouldShowProgressBar])

  useEffect(() => {
    // hack to disable ios pwa prompt for https://github.com/stackernews/stacker.news/issues/953
    // see https://github.com/chrisdancee/react-ios-pwa-prompt/blob/66e91c4f033b740cff42c3220cf13ebdf39e3078/src/index.js#L30
    if (router?.query?.disablePrompt) {
      window.localStorage.setItem('iosPwaPrompt', JSON.stringify({ isiOS: false, visits: 0 }))
    }
  }, [router?.query?.disablePrompt])

  /*
    If we are on the client, we populate the apollo cache with the
    ssr data
  */
  const { apollo, ssrData, ssrPublicCookies, me, price, blockHeight, siteSettings, branding, ...otherProps } = props
  useEffect(() => {
    writeQuery(client, apollo, ssrData)
  }, [client, apollo, ssrData])

  return (
    <>
      <Head>
        <meta name='viewport' content='initial-scale=1.0, width=device-width, viewport-fit=cover' />
      </Head>
      <ErrorBoundary>
        <PlausibleProvider>
          <ApolloProvider client={client}>
            <BrandingProvider branding={branding}>
              <MeProvider me={me}>
                <CookiesProvider ssrPublicCookies={ssrPublicCookies}>
                  <WalletsProvider>
                    <HasNewNotesProvider>
                      <FaviconProvider>
                        <ServiceWorkerProvider>
                          <PriceProvider price={price}>
                            <AnimationProvider>
                              <ToastProvider>
                                <ShowModalProvider>
                                  <BlockHeightProvider blockHeight={blockHeight}>
                                    <SiteSettingsProvider settings={siteSettings}>
                                      <ErrorBoundary>
                                        <Component ssrData={ssrData} {...otherProps} />
                                        {!router?.query?.disablePrompt && <PWAPrompt copyBody='This website has app functionality. Add it to your home screen to use it in fullscreen and receive notifications. In Safari:' promptOnVisit={2} />}
                                      </ErrorBoundary>
                                    </SiteSettingsProvider>
                                  </BlockHeightProvider>
                                </ShowModalProvider>
                              </ToastProvider>
                            </AnimationProvider>
                          </PriceProvider>
                        </ServiceWorkerProvider>
                      </FaviconProvider>
                    </HasNewNotesProvider>
                  </WalletsProvider>
                </CookiesProvider>
              </MeProvider>
            </BrandingProvider>
          </ApolloProvider>
        </PlausibleProvider>
      </ErrorBoundary>
    </>
  )
}
